import { z } from "zod";
import { bilingualStringSchema } from "./bilingual.ts";

export const subscriptionTierSchema = z.enum([
	"trial",
	"pro",
	"premium",
	"enterprise",
]);
export type SubscriptionTier = z.infer<typeof subscriptionTierSchema>;

/** Billing cadence — one renewal extends by 1 / 3 / 12 months respectively. */
export const billingIntervalSchema = z.enum(["monthly", "quarterly", "yearly"]);
export type BillingInterval = z.infer<typeof billingIntervalSchema>;

/** Months one renewal of each interval adds. */
export const BILLING_INTERVAL_MONTHS: Record<BillingInterval, number> = {
	monthly: 1,
	quarterly: 3,
	yearly: 12,
};

/**
 * The kind of healthcare facility a tenant is. Drives which dashboard/module
 * the staff see after login:
 *   - clinic    → doctor practice (the original product)
 *   - lab       → standalone laboratory (walk-ins, results, WhatsApp delivery)
 *   - pharmacy  → pharmacy (inventory + dispensing)
 *   - radiology → imaging center (X-ray, CT, MRI, US, …)
 * Existing tenants default to 'clinic'.
 */
export const facilityTypeSchema = z.enum([
	"clinic",
	"lab",
	"pharmacy",
	"radiology",
]);
export type FacilityType = z.infer<typeof facilityTypeSchema>;

export const businessTypeSchema = z.enum([
	"cosmetic",
	"dermatology",
	"dental",
	"orthodontics",
	"hair_transplant",
	"general",
	"pediatric",
	"gynecology",
	"cardiology",
	"internal_medicine",
	"ophthalmology",
	"physiotherapy",
	"orthopedics",
	"ent",
	"psychiatry",
	"other",
]);
export type BusinessType = z.infer<typeof businessTypeSchema>;

export const clinicBrandingSchema = z.object({
	logo_url: z.string().url().optional(),
	primary_color: z
		.string()
		.regex(/^#[0-9a-fA-F]{6}$/)
		.optional(),
	accent_color: z
		.string()
		.regex(/^#[0-9a-fA-F]{6}$/)
		.optional(),
	cover_image_url: z.string().url().optional(),
});
export type ClinicBranding = z.infer<typeof clinicBrandingSchema>;

export const clinicSettingsSchema = z.object({
	default_appointment_duration_min: z.number().int().positive().default(30),
	default_buffer_min: z.number().int().nonnegative().default(5),
	allow_walkin: z.boolean().default(true),
	allow_online_booking: z.boolean().default(true),
	appointment_lead_time_hours: z.number().int().nonnegative().default(0),
	cancellation_window_hours: z.number().int().nonnegative().default(24),
	no_show_threshold: z.number().int().positive().default(3),
	voice_ai_enabled: z.boolean().default(true),
	voice_ai_minutes_quota: z.number().int().nonnegative().default(120),
	whatsapp_enabled: z.boolean().default(true),
	instagram_enabled: z.boolean().default(false),
	queue_display_enabled: z.boolean().default(true),
});
export type ClinicSettings = z.infer<typeof clinicSettingsSchema>;

export const clinicSchema = z.object({
	id: z.string().uuid(),
	slug: z
		.string()
		.min(2)
		.max(50)
		.regex(/^[a-z0-9-]+$/),
	name: bilingualStringSchema,
	facility_type: facilityTypeSchema.default("clinic"),
	business_type: businessTypeSchema,
	timezone: z.string().default("Asia/Baghdad"),
	default_language: z.enum(["ar", "en"]).default("ar"),
	rtl_default: z.boolean().default(true),
	subscription_tier: subscriptionTierSchema,
	trial_ends_at: z.string().datetime().nullable(),
	subscribed_until: z.string().datetime().nullable(),
	is_active: z.boolean(),
	/** Inpatient/HIS capability (opt-in). Optional → omitted = off. */
	inpatient_enabled: z.boolean().optional(),
	branding: clinicBrandingSchema.nullable(),
	settings: clinicSettingsSchema,
	feature_flags: z.record(z.string(), z.boolean()),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type Clinic = z.infer<typeof clinicSchema>;
